"use client";

import { useEffect, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import "@fullcalendar/core/index.cjs";
import "@fullcalendar/daygrid/index.cjs";
import "./fullcalendar-overrides.css";
import { FetchEvents } from "../../components/FetchEvents";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../config/firebase";
import Button from "../../components/Button";
import { Pinyon_Script } from "next/font/google";
import ContentSwitchEditor from "../../ultemplateLib/CMS/ContentSwitchEditor";
import Footer from "../../components/Footer";

const pinyonScript = Pinyon_Script({ subsets: ["latin"], weight: "400" });

function formatDateForGoogleCalendar(dateStr) {
  const date = new Date(dateStr);
  return date.toISOString().replace(/-|:|\.\d+/g, "");
}

function generateGoogleCalendarUrl(event) {
  if (!event) return "#";

  const start = formatDateForGoogleCalendar(event.start);
  let end;

  if (event.end) {
    end = formatDateForGoogleCalendar(event.end);
  } else {
    const startDate = new Date(event.start);
    end = new Date(startDate.getTime() + 60 * 60 * 1000)
      .toISOString()
      .replace(/-|:|\.\d+/g, "");
  }

  const details = encodeURIComponent(event.description || "");
  const location = encodeURIComponent(event.location || "");
  const title = encodeURIComponent(event.title || "Event");

  return `https://www.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&details=${details}&location=${location}&sf=true&output=xml`;
}

export default function CalendarClient({ pageId, initialSectionsById }) {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [allEvents, setAllEvents] = useState([]);
  const [filter, setFilter] = useState("event");
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [pageContentEdit, setPageContentEdit] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) return;

      try {
        const token = await user.getIdToken();
        await fetch(`${process.env.NEXT_PUBLIC_REACT_APP_SERVER}/users/self`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
      } catch (error) {
        console.error("Error checking user permissions:", error);
      }
    });

    async function loadEvents() {
      try {
        const data = await FetchEvents();
        setAllEvents(
          data.map((ev) => ({
            id: ev.eventId,
            title: ev.title,
            start: `${ev.start_date}T${ev.start_time}`,
            end:
              ev.end_date && ev.end_time
                ? `${ev.end_date}T${ev.end_time}`
                : undefined,
            description: ev.description,
            location: ev.location,
            type: ev.type,
            classNames: [
              ev.type === "class" ? "event-type-class" : "event-type-event",
            ],
          })),
        );
      } catch (error) {
        console.error("Error fetching:", error);
      }
    }

    loadEvents();
    return () => unsubscribe();
  }, []);

  const filteredEvents =
    filter === "all" ? allEvents : allEvents.filter((ev) => ev.type === filter);

  return (
    <>
      <main className="p-12">
        <h1
          className={`${pinyonScript.className} text-center mb-8 text-5xl md:text-7xl text-balance`}
        >
          Calendar
        </h1>

        <section className="max-w-3xl mx-auto mb-8">
          <ContentSwitchEditor
            sectionId="section1"
            section={initialSectionsById?.section1}
            pageId={pageId}
            isEditing={pageContentEdit}
            className=""
          />
        </section>

        <div className="filter-buttons mb-4 flex flex-wrap">
          {["all", "event", "class"].map((filterName) => (
            <button
              key={filterName}
              onClick={() => setFilter(filterName)}
              className={filter === filterName ? "active" : ""}
            >
              {filterName.charAt(0).toUpperCase() + filterName.slice(1)}
            </button>
          ))}
        </div>

        <div className="max-w-full overflow-x-auto touch-auto">
          <FullCalendar
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            contentHeight="auto"
            eventDisplay="block"
            events={filteredEvents}
            eventClick={(info) => {
              setSelectedEvent({
                id: info.event.id,
                title: info.event.title,
                description: info.event.extendedProps.description,
                start: info.event.startStr,
                end: info.event.endStr,
                location: info.event.extendedProps.location,
                type: info.event.extendedProps.type,
              });
            }}
          />
        </div>

        {selectedEvent && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedEvent(null)}
          >
            <div
              className="relative bg-white rounded-lg shadow-lg p-6 w-full max-w-md sm:max-w-lg max-h-[80vh] overflow-y-auto"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 text-3xl font-bold"
                onClick={() => setSelectedEvent(null)}
                aria-label="Close"
              >
                &times;
              </button>

              <h3 className="text-xl font-bold mb-2">{selectedEvent.title}</h3>
              <p className="mb-2">
                Location: {selectedEvent.location || "Login to see location"}
              </p>

              <div className="relative">
                <p
                  className={`md:text-lg py-2 break-words whitespace-pre-wrap transition-all duration-300 ease-in-out ${
                    showFullDescription
                      ? "max-h-full"
                      : "max-h-[100px] overflow-hidden"
                  }`}
                >
                  {selectedEvent.description}
                </p>
                {selectedEvent.description.length > 200 && (
                  <button
                    onClick={() => setShowFullDescription((prev) => !prev)}
                    className="text-blue-600 my-2 underline hover:text-blue-800"
                  >
                    {showFullDescription ? "See less" : "See more"}
                  </button>
                )}
              </div>

              <Button
                type=""
                href={`./listing?eventId=${selectedEvent.id}&eventType=${selectedEvent.type}`}
                className="w-full mt-4"
              >
                Find Out More
              </Button>
              <a
                href={generateGoogleCalendarUrl(selectedEvent)}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center mt-3 px-4 py-2 bg-white text-black border-2 border-black rounded hover:bg-yellow-50 transition"
              >
                Add to Google Calendar
              </a>
            </div>
          </div>
        )}
      </main>

      <Footer
        pageEditable={true}
        pageContentEdit={pageContentEdit}
        toggleContentEdit={() => setPageContentEdit((prev) => !prev)}
      />
    </>
  );
}
